import type { ReactNode, Ref } from "react";

import { caption, tile, value, valueGold } from "~/components/KpiTile.css";

export interface KpiTileProps {
  /** The number itself. Rendered in the display ramp with tabular figures. */
  value: ReactNode;
  /** What the number counts — always present; a bare number is a riddle. */
  label: string;
  /**
   * Ceremony treatment: paints the numeral gold. At most one tile in a
   * strip should set this, and only for a number worth celebrating —
   * gold is never a functional status.
   */
  gold?: boolean;
  /**
   * Test handle placed on the NUMBER, not the tile: the caption is already
   * findable as text, but the numeral is a bare span with no role or label
   * of its own, so a test that has to read the value has nothing else to
   * hold. Opt-in — a tile nobody asserts on stays attribute-free.
   */
  testId?: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * A single headline number with its caption, sized to share a strip of
 * 4–6 equal-width siblings. Layout is the caller's: drop these in a flex
 * row and each one claims an equal share.
 */
export function KpiTile({ value: kpiValue, label, gold, testId, ref }: KpiTileProps) {
  return (
    <div ref={ref} className={tile}>
      <span className={gold ? valueGold : value} data-testid={testId}>
        {kpiValue}
      </span>
      <span className={caption}>{label}</span>
    </div>
  );
}
